import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';
import { 
  LifeBuoy, 
  Book, 
  MessageCircle, 
  Mail, 
  Search,
  HelpCircle,
  CreditCard,
  Calendar,
  Users,
  Shield,
  Settings,
  ChevronRight
} from 'lucide-react';

const Support = () => {
  const isIOSApp = useIsIOSApp();
  const [searchQuery, setSearchQuery] = useState('');

  const allCategories = [
    {
      icon: HelpCircle,
      title: 'Getting Started',
      description: 'New to ExpatsSphere? Learn the basics',
      link: '/faq#getting-started',
      color: 'bg-blue-500'
    },
    {
      icon: CreditCard,
      title: 'Membership & Billing',
      description: 'Payments, subscriptions, and refunds',
      link: '/faq#membership',
      color: 'bg-green-500'
    },
    {
      icon: Calendar,
      title: 'Events',
      description: 'Creating and attending events',
      link: '/faq#events',
      color: 'bg-purple-500'
    },
    {
      icon: Users,
      title: 'Connections & Messaging',
      description: 'Connect with other expats',
      link: '/faq#connections',
      color: 'bg-pink-500'
    },
    {
      icon: Shield,
      title: 'Privacy & Safety',
      description: 'Your data and security',
      link: '/faq#privacy',
      color: 'bg-amber-500'
    },
    {
      icon: Settings,
      title: 'Account Settings',
      description: 'Profile, notifications, preferences',
      link: '/faq#account',
      color: 'bg-cyan-500'
    }
  ];
  const categories = isIOSApp ? allCategories.filter(c => c.title !== 'Membership & Billing') : allCategories;

  const allQuickLinks = [
    { title: 'How do I create an event?', link: '/faq#create-event' },
    { title: 'How do I upgrade to membership?', link: '/faq#membership' },
    { title: 'How do I connect with other expats?', link: '/faq#connections' },
    { title: 'How do I change my privacy settings?', link: '/faq#privacy' },
    { title: 'How do I delete my account?', link: '/faq#account' }
  ];
  const quickLinks = isIOSApp ? allQuickLinks.filter(l => !l.title.toLowerCase().includes('membership')) : allQuickLinks;

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary to-accent rounded-full mb-6 shadow-lg">
                <LifeBuoy className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">How Can We Help?</h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto mb-8">
                Find answers to common questions or get in touch with our support team.
              </p>

              {/* Search */}
              <div className="max-w-xl mx-auto relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                <Input
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search for help..."
                  className="h-14 pl-12 pr-4 rounded-full border-2 text-lg"
                />
              </div>
            </div>

            {/* Categories Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-12">
              {categories.map((category, index) => (
                <motion.div
                  key={category.title}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                >
                  <Link to={category.link}>
                    <div className="bg-white rounded-2xl p-6 border-2 border-border hover:border-primary/50 transition-colors group">
                      <div className={`w-12 h-12 ${category.color} rounded-xl flex items-center justify-center mb-4`}>
                        <category.icon className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-lg font-bold mb-2 group-hover:text-primary transition-colors">
                        {category.title}
                      </h3>
                      <p className="text-muted-foreground text-sm">{category.description}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            {/* Quick Links & Contact */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Popular Questions */}
              <div className="bg-white rounded-3xl p-8 border-2 border-border">
                <div className="flex items-center gap-3 mb-6">
                  <Book className="w-6 h-6 text-primary" />
                  <h2 className="text-xl font-heading font-bold">Popular Questions</h2>
                </div>
                <div className="space-y-3">
                  {quickLinks.map((link, index) => (
                    <Link key={index} to={link.link}>
                      <div className="flex items-center justify-between p-4 rounded-xl hover:bg-muted/50 transition-colors group">
                        <span className="text-muted-foreground group-hover:text-foreground transition-colors">
                          {link.title}
                        </span>
                        <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
                      </div>
                    </Link>
                  ))}
                </div>
                <Link to="/faq">
                  <Button variant="outline" className="w-full mt-4 rounded-full">
                    View All FAQs
                  </Button>
                </Link>
              </div>

              {/* Contact Support */}
              <div className="bg-gradient-to-br from-primary to-accent rounded-3xl p-8 text-white">
                <div className="flex items-center gap-3 mb-6">
                  <MessageCircle className="w-6 h-6" />
                  <h2 className="text-xl font-heading font-bold">Still Need Help?</h2>
                </div>
                <p className="text-white/90 mb-6">
                  Can't find what you're looking for? Our support team is here to help you 
                  with any questions about ExpatsSphere.
                </p>
                
                <div className="space-y-4 mb-6">
                  <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                    <Mail className="w-5 h-5" />
                    <div>
                      <div className="font-medium">Email Support</div>
                      <div className="text-sm text-white/80">Support@expatssphere.com</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 bg-white/10 rounded-xl p-4">
                    <LifeBuoy className="w-5 h-5" />
                    <div>
                      <div className="font-medium">Response Time</div>
                      <div className="text-sm text-white/80">Usually within 24 hours</div>
                    </div>
                  </div>
                </div>

                <Link to="/contact">
                  <Button className="w-full bg-white text-primary hover:bg-white/90 rounded-full font-bold">
                    Contact Support
                  </Button>
                </Link>
              </div>
            </div>

            {/* Community Resources */}
            <div className="mt-12 text-center">
              <h2 className="text-2xl font-heading font-bold mb-4">Community Resources</h2>
              <p className="text-muted-foreground mb-6">
                Explore our guides and articles to make the most of your expat journey.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link to="/guides">
                  <Button variant="outline" className="rounded-full px-8">
                    <Book className="w-4 h-4 mr-2" />
                    Expat Guides
                  </Button>
                </Link>
                <Link to="/groups">
                  <Button variant="outline" className="rounded-full px-8">
                    <Users className="w-4 h-4 mr-2" />
                    Interest Groups
                  </Button>
                </Link>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Support;
